import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch } from 'app/store'
import {
  Assignment,
  AssignmentPage,
  AssignmentBodyItem,
  QuestionBodyItem
} from 'interfaces/assignments'
import { fetchAssignmentData } from 'services/assignments.api'

export type StudentSubmissionAnswer = {
  correct: boolean
  answer: string
  question_id: number
}

export type StudentSubmissionAttempt = {
  test_date: string
  answers: StudentSubmissionAnswer[]
}

export type StudentSubmissionData = {
  student: {
    firstName: string
    lastName: string
    studentId: number
  }
  attempts: StudentSubmissionAttempt[]
}

const DEFAULT_STUDENT_SUBMISSION: StudentSubmissionData = {
  student: {
    firstName: 'Gordon',
    lastName: 'Sumner',
    studentId: 29629
  },
  attempts: [
    {
      test_date: '2024-09-01',
      answers: [
        {
          correct: true,
          answer: '1',
          question_id: 421
        }
      ]
    }
  ]
}

interface InitialState {
  assignment: Assignment | null
  pageIdx: number
  allow_calculator: boolean
  allow_ti84_calculator: boolean
  other_tools: boolean
  allowed_monitoring?: boolean
  text_to_speech_enabled?: boolean
  currentPage: AssignmentPage | null
  hasSaveError: boolean
  enableStudentSubmissionView?: boolean
  studentSubmission: StudentSubmissionData
  currentAttempt: StudentSubmissionAttempt | null
}

const initialState: InitialState = {
  assignment: null,
  pageIdx: 0,
  allow_calculator: false,
  allow_ti84_calculator: false,
  other_tools: false,
  allowed_monitoring: false,
  text_to_speech_enabled: false,
  currentPage: null,
  hasSaveError: false,
  enableStudentSubmissionView: false,
  studentSubmission: DEFAULT_STUDENT_SUBMISSION,
  currentAttempt: DEFAULT_STUDENT_SUBMISSION.attempts[0] || null
}

const getCurrentPage = (assignment: Assignment | null, pageIdx: number) =>
  assignment?.pages?.[pageIdx] || null

const getPageIndexById = (assignment: Assignment | null, pageId: number) => {
  if (!assignment?.pages?.length) {
    return -1
  }

  return assignment.pages.findIndex(
    (page: AssignmentPage) => page.id === pageId
  )
}

const clonePage = (page: AssignmentPage | null) =>
  page ? (JSON.parse(JSON.stringify(page)) as AssignmentPage) : null

const parsePenNoteMarkings = (rawValue: any) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null
  }

  let parsedValue = rawValue

  // Backend may return plain JSON, stringified JSON, or double-stringified JSON.
  for (let parseAttempt = 0; parseAttempt < 2; parseAttempt += 1) {
    if (typeof parsedValue !== 'string') {
      break
    }

    try {
      parsedValue = JSON.parse(parsedValue)
    } catch {
      return null
    }
  }

  return parsedValue ?? null
}

const normalizeAssignmentPenNoteMarkings = (responsePayload: any) => {
  const assignmentData = responsePayload?.assignment

  if (!assignmentData?.pages?.length) {
    return responsePayload
  }

  const normalizedPages = assignmentData.pages.map((page: any) => {
    /**
     * Assignment question-set items can return the student's saved response as
     * `student_answer`, but the existing student rendering flow reads `answer`.
     * Normalize that field here so returning students see their prior choice
     * through the same path used after they answer in-session.
     */
    const normalizedBodyItems = (page?.body_items || []).map(
      (bodyItem: any) => {
        if (bodyItem?.body_item_type !== 'question') {
          return bodyItem
        }

        const parsedStudentAnswer = Number(bodyItem?.student_answer)

        return {
          ...bodyItem,
          answer: Number.isNaN(parsedStudentAnswer)
            ? bodyItem?.answer
            : parsedStudentAnswer
        }
      }
    )

    return {
      ...page,
      body_items: normalizedBodyItems,
      student_pen_note_markings: parsePenNoteMarkings(
        page?.student_pen_note_markings
      ),
      student_pen_markings: parsePenNoteMarkings(page?.student_pen_markings)
    }
  })

  return {
    ...responsePayload,
    assignment: {
      ...assignmentData,
      pages: normalizedPages
    }
  }
}

const updateQuestionAnswerInBodyItems = (
  bodyItems: (AssignmentBodyItem | QuestionBodyItem)[],
  questionId: number,
  answer: number | null
) => {
  /**
   * Assignment pages mix content blocks and question blocks, so answer updates
   * must only touch the matching question item while leaving all other body
   * items unchanged.
   */
  return bodyItems.map((item) => {
    if (
      item.id !== questionId ||
      item.body_item_type !== 'question' ||
      !('answer' in item)
    ) {
      return item
    }

    return {
      ...item,
      answer
    }
  })
}

const clearAssignmentAnswersInBodyItems = (
  bodyItems: (AssignmentBodyItem | QuestionBodyItem)[]
) => {
  /**
   * Teacher-driven clear events should reset only question answers while
   * preserving all non-question body items and page structure as-is.
   */
  return bodyItems.map((item) => {
    if (item.body_item_type !== 'question' || !('answer' in item)) {
      return item
    }

    return {
      ...item,
      answer: null
    }
  })
}

const assignmentSlice = createSlice({
  name: 'assignmentReducer',
  initialState,
  reducers: {
    setAssignmentData: (state, action: PayloadAction<any>) => {
      const assignmentData = action.payload.assignment
      const assignmentMeta = action.payload

      state.assignment = assignmentData
      state.allow_calculator = assignmentMeta.allow_calculator
      state.allow_ti84_calculator = assignmentMeta.allow_ti84_calculator
      /**
       * Assignment tool visibility should only turn on when the backend
       * explicitly enables `other_tools`. Missing or undefined values must
       * keep those tools hidden.
       */
      state.other_tools = assignmentMeta.other_tools === true
      state.allowed_monitoring = assignmentMeta.allowed_monitoring
      state.text_to_speech_enabled = assignmentMeta.text_to_speech_enabled
      state.pageIdx = 0
      state.currentPage = clonePage(getCurrentPage(assignmentData, 0))
      state.hasSaveError = false
    },
    setAssignmentPageId: (state, action: PayloadAction<number>) => {
      const resolvedPageIdx = getPageIndexById(state.assignment, action.payload)

      if (resolvedPageIdx < 0) {
        return
      }

      state.pageIdx = resolvedPageIdx
      state.currentPage = clonePage(
        getCurrentPage(state.assignment, resolvedPageIdx)
      )
      state.hasSaveError = false
    },
    updateAssignmentPageAction: (
      state,
      action: PayloadAction<AssignmentPage | null>
    ) => {
      state.currentPage = action.payload
    },
    updateAssignmentQuestionAnswer: (
      state,
      action: PayloadAction<{
        pageId: number
        questionId: number
        answer: number | null
      }>
    ) => {
      const { pageId, questionId, answer } = action.payload

      /**
       * Keep both page references aligned so the active question view and the
       * backing assignment collection stay in sync across navigation.
       */
      if (state.assignment?.pages?.length) {
        state.assignment.pages = state.assignment.pages.map((page) => {
          if (page.id !== pageId) {
            return page
          }

          return {
            ...page,
            body_items: updateQuestionAnswerInBodyItems(
              page.body_items,
              questionId,
              answer
            )
          }
        })
      }

      if (state.currentPage?.id === pageId) {
        state.currentPage = {
          ...state.currentPage,
          body_items: updateQuestionAnswerInBodyItems(
            state.currentPage.body_items,
            questionId,
            answer
          )
        }
      }
    },
    clearCurrentAssignmentPageAnswers: (state) => {
      if (!state.currentPage?.body_items?.length) {
        return
      }

      const clearedBodyItems = clearAssignmentAnswersInBodyItems(
        state.currentPage.body_items
      )

      state.currentPage = {
        ...state.currentPage,
        body_items: clearedBodyItems
      }

      if (state.assignment?.pages?.length) {
        state.assignment.pages = state.assignment.pages.map((page) => {
          if (page.id !== state.currentPage?.id) {
            return page
          }

          return {
            ...page,
            body_items: clearedBodyItems
          }
        })
      }
    },
    clearAllAssignmentAnswers: (state) => {
      if (!state.assignment?.pages?.length) {
        return
      }

      /**
       * Teacher clear-events apply to the full assignment, so every page copy
       * must be reset to keep navigation and the active page in sync.
       */
      state.assignment.pages = state.assignment.pages.map((page) => ({
        ...page,
        body_items: clearAssignmentAnswersInBodyItems(page.body_items)
      }))

      state.currentPage = clonePage(
        getCurrentPage(state.assignment, state.pageIdx)
      )
    },
    enableStudentSubmissionView: (state, action: PayloadAction<boolean>) => {
      state.enableStudentSubmissionView = Boolean(action.payload)
    },
    setStudentSubmission: (
      state,
      action: PayloadAction<StudentSubmissionData>
    ) => {
      state.studentSubmission = action.payload
      state.currentAttempt = action.payload?.attempts?.[0] || null
    },
    setCurrentStudentSubmissionAttempt: (
      state,
      action: PayloadAction<StudentSubmissionAttempt | null>
    ) => {
      state.currentAttempt = action.payload || null
    },
    saveAssignmentPageSuccess: (
      state,
      action: PayloadAction<AssignmentPage | null>
    ) => {
      state.currentPage = action.payload
      state.hasSaveError = false
    },
    saveAssignmentPageError: (state) => {
      state.currentPage = clonePage(
        getCurrentPage(state.assignment, state.pageIdx)
      )
      state.hasSaveError = true
    },
    clearAssignmentPageSaveError: (state) => {
      state.hasSaveError = false
    },
    clearAssignmentData: () => initialState
  }
})

export const {
  setAssignmentData,
  setAssignmentPageId,
  updateAssignmentPageAction,
  updateAssignmentQuestionAnswer,
  clearCurrentAssignmentPageAnswers,
  clearAllAssignmentAnswers,
  enableStudentSubmissionView,
  setStudentSubmission,
  setCurrentStudentSubmissionAttempt,
  saveAssignmentPageSuccess,
  saveAssignmentPageError,
  clearAssignmentPageSaveError,
  clearAssignmentData
} = assignmentSlice.actions

export const SelectAssignmentState = (state: any) => state.assignmentReducer

export default assignmentSlice.reducer

export const getAssignmentDataAction =
  (assignmentId: number | string, classroomId?: number | string) =>
  async (dispatch: AppDispatch) => {
    try {
      const data = await fetchAssignmentData(assignmentId, classroomId)

      // test conditions for pen note markings - to be removed once backend is consistent in its response format
      // data.data.assignment.toc.main[0].pages[0].is_bookmarked = true
      // data.data.assignment.toc.main[0].pages[0].has_notes = true
      // data.data.assignment.toc.main[1].pages[0].is_bookmarked = true
      // data.data.assignment.toc.main[1].page_questions[0].has_notes = true
      // data.data.assignment.toc.main[3].page_questions[0].is_bookmarked = true
      // data.data.assignment.toc.main[3].page_questions[0].has_notes = true
      const normalizedResponse = normalizeAssignmentPenNoteMarkings(data.data)
      dispatch(setAssignmentData(normalizedResponse))

      return normalizedResponse
    } catch {
      return null
    }
  }
